/**
 * GoodsReceiptFormModal — yeni mal kabul (irsaliye) oluşturma formu.
 *
 * Tedarikçi + depo seçilir, ardından satırlar eklenir. Her satır YA bir
 * hammadde (Ingredient) YA bir hazır ürün (Product) taşır — ikisi için de
 * server-side aranan picker modallar kullanılır (binlerce kayıtta DOM
 * şişmesin). DRAFT olarak kaydedilir; stok/maliyet ancak "Onayla" ile işler.
 *
 * Backend: POST /api/v1/goods-receipts/
 *   { supplier, location, delivery_note_number?, notes?,
 *     lines: [{ingredient?|product?, quantity_received, unit_price_minor?}] }
 */
import { useEffect, useState } from "react";
import {
  ActionIcon, Badge, Button, Checkbox, Group, Modal, NumberInput, SegmentedControl,
  Select, Stack, Table, Text, TextInput,
} from "@mantine/core";
import { IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useResource, useCreate } from "../../../shared/api";
import { MoneyDisplay } from "../../../shared/ui";
import { IngredientPickerModal } from "./IngredientPickerModal";
import { ProductPickerModal } from "./ProductPickerModal";
import { SupplierPickerModal } from "./SupplierPickerModal";
import { SupplyItemPickerModal } from "./SupplyItemPickerModal";
import { purchaseUnitsFor, factorFor, lineToPayload } from "./goodsReceiptUnits";

/** PO'dan önceden doldurulmuş satır (receivable-lines yanıtından).
 *
 * PO satırı YA ingredient (mutfak) YA supply_item (mutfak-dışı) taşır; biri dolu
 * olur, diğeri boş/null. `item_kind` hangisinin geçerli olduğunu söyler — prefill
 * doğru DraftLine tipini (INGREDIENT/SUPPLY_ITEM) bundan türetir.
 */
export interface PrefilledGrLine {
  po_line_id: string;
  ingredient_id: string | null;
  ingredient_name: string;
  supply_item_id?: string | null;
  supply_item_name?: string;
  item_kind?: "ingredient" | "supply_item";
  unit: string;
  quantity_remaining: string;
  unit_price_minor: number;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** PO'dan açıldığında: tedarikçi kilitli + satırlar önceden dolu. */
  prefill?: {
    supplierId: string;
    supplierName: string;
    purchaseOrderId: string;
    lines: PrefilledGrLine[];
  } | null;
}

/** Forma eklenmiş bir mal kabul satırı.
 *
 * Kullanıcı miktar ve fiyatı ALIŞ birimi (`purchaseUnit`) cinsinden girer.
 * Stok birimi (`stockUnit`) ve backend'e gidecek değerler submit'te bundan
 * türetilir — bkz. handleSubmit. Böylece "1 kg = 123 ₺" girişi, stok gram
 * cinsinden tutulsa bile doğru toplam ve maliyet üretir.
 */
type LineType = "INGREDIENT" | "PRODUCT" | "SUPPLY_ITEM";
interface DraftLine {
  /** Geçici satır id'si (React key). */
  key: string;
  type: LineType;
  refId: string;
  refName: string;
  /** Hammaddenin/ürünün STOK birimi (stok ve backend bu birimde). */
  stockUnit: string;
  /** Kullanıcının fiyatı/miktarı girdiği ALIŞ birimi (faturayla aynı). */
  purchaseUnit: string;
  /** Miktar — alış birimi cinsinden (örn. 1 = 1 kg). */
  quantity: number | string;
  /** Birim fiyat — alış birimi başına kuruş (örn. 12300 = 123 ₺/kg). */
  purchasePriceMinor: number | string;
  /** PO'dan geldiyse bağlı PO satırı (kalan miktar takibi için). */
  poLineId?: string;
  /** PO'dan geldiyse o partide kalan azami miktar (stok birimi). */
  maxRemaining?: number;
  /** Bu sevkiyata DAHİL mi? İşaretsiz satır kaydedilmez (formda kalır). */
  selected: boolean;
}

export function GoodsReceiptFormModal({ opened, onClose, prefill }: Props) {
  const qc = useQueryClient();
  const { data: locationsData } = useResource<any>("stock-locations");
  const create = useCreate("goods-receipts");

  const locations: any[] = locationsData?.results || locationsData || [];
  const isPrefilled = !!prefill;

  // Tedarikçi server-side aranan picker'dan seçilir — düz dropdown büyük
  // tedarikçi listesinde patlardı. Seçilen ad gösterim için ayrıca tutulur.
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState("");
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);

  // PO'dan açıldığında: tedarikçiyi kilitle ve satırları kalan miktar + PO
  // fiyatıyla doldur. Prefill'de alış birimi = stok birimi (1:1, dönüşüm yok)
  // — receivable-lines zaten stok birimi miktarı ve birim fiyatı döndürür.
  useEffect(() => {
    if (!opened || !prefill) return;
    setSupplierId(prefill.supplierId);
    setSupplierName(prefill.supplierName);
    setDeliveryNote("");
    setLines(
      prefill.lines.map((l) => {
        const remaining = Number(l.quantity_remaining);
        // PO satırının kalem tipini koru — supply_item satırını INGREDIENT
        // olarak prefill etmek payload'ı bozardı (ingredient_id null → backend
        // XOR ihlali / yanlış FK). item_kind yoksa eski davranış: ingredient.
        const isSupply = l.item_kind === "supply_item";
        return {
          key: `pl-${l.po_line_id}`,
          type: (isSupply ? "SUPPLY_ITEM" : "INGREDIENT") as LineType,
          refId: (isSupply ? l.supply_item_id : l.ingredient_id) || "",
          refName: (isSupply ? l.supply_item_name : l.ingredient_name) || "",
          stockUnit: l.unit,
          purchaseUnit: l.unit, // 1:1 — backend stok birimi miktar verdi
          quantity: remaining,
          purchasePriceMinor: l.unit_price_minor,
          poLineId: l.po_line_id,
          maxRemaining: remaining,
          selected: true,
        };
      }),
    );
  }, [opened, prefill]);

  // Yeni satır taslağı — picker tipini ve seçilen referansı tutar.
  const [newType, setNewType] = useState<LineType>("INGREDIENT");
  const [ingPickerOpen, setIngPickerOpen] = useState(false);
  const [prodPickerOpen, setProdPickerOpen] = useState(false);
  const [supplyPickerOpen, setSupplyPickerOpen] = useState(false);

  function resetForm() {
    setSupplierId(null);
    setSupplierName("");
    setLocationId(null);
    setDeliveryNote("");
    setLines([]);
    setNewType("INGREDIENT");
  }

  function addLine(type: LineType, refId: string, refName: string, unit: string) {
    // Aynı hammadde/ürün ikinci kez eklenmesin.
    if (lines.some((l) => l.refId === refId)) {
      notifications.show({ message: `${refName} zaten eklenmiş`, color: "orange" });
      return;
    }
    // Varsayılan alış birimi: dönüşümlü birimlerde (gr/ml) faturayla uyumlu
    // büyük birimi (kg/lt) öner — kullanıcı "1 kg = X ₺" girmeye alışkın.
    const options = purchaseUnitsFor(unit);
    const defaultPurchaseUnit = options[options.length - 1].value;
    setLines((prev) => [
      ...prev,
      {
        key: `${refId}-${Date.now()}`,
        type, refId, refName,
        stockUnit: unit,
        purchaseUnit: defaultPurchaseUnit,
        quantity: 1,
        purchasePriceMinor: 0,
        selected: true,
      },
    ]);
  }

  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }

  async function handleSubmit() {
    if (!supplierId) {
      notifications.show({ message: "Tedarikçi seçin", color: "orange" });
      return;
    }
    if (!locationId) {
      notifications.show({ message: "Depo seçin", color: "orange" });
      return;
    }
    if (lines.length === 0) {
      notifications.show({ message: "En az bir satır ekleyin", color: "orange" });
      return;
    }
    // Parçalı teslimat: yalnız İŞARETLİ ve miktarı 0'dan büyük satırlar bu
    // partiye dahil edilir. İşaretsiz satır (checkbox kapalı) "bu sevkiyatta
    // gelmedi" demek — kaydedilmez ama formda kalır (PO açık kalır, sonra alınır).
    const activeLines = lines.filter((l) => l.selected && Number(l.quantity) > 0);
    if (activeLines.length === 0) {
      notifications.show({
        message: "Kaydetmek için en az bir satırı işaretleyip miktar girin",
        color: "orange",
      });
      return;
    }
    // Kalan miktar aşımı kontrolü (PO'dan gelen satırlar için).
    const over = activeLines.find(
      (l) => l.maxRemaining != null && Number(l.quantity) > l.maxRemaining + 1e-9,
    );
    if (over) {
      notifications.show({
        message: `${over.refName}: girilen miktar kalandan (${over.maxRemaining}) fazla`,
        color: "red",
      });
      return;
    }
    // Alış birimi → stok birimi dönüşümü. Backend quantity_received ve
    // unit_price_minor'ı STOK birimi cinsinden bekler (örn. gram, gram-başı
    // kuruş). Önce satır TOPLAMINI kuruş olarak kesin hesapla, sonra stok
    // birimi başına fiyatı toplamdan geri türet — böylece "miktar×birim =
    // toplam" her zaman tutar, yuvarlama yalnız stok-birimi maliyetinde
    // ihmal edilebilir düzeyde kalır.
    const payloadLines = activeLines.map((l) => {
      const { quantityReceived, unitPriceMinor } = lineToPayload({
        stockUnit: l.stockUnit,
        purchaseUnit: l.purchaseUnit,
        quantity: Number(l.quantity),
        purchasePriceMinor: Number(l.purchasePriceMinor),
      });
      // Kalem tipi → FK alanı (XOR). Backend ingredient/product/supply_item bekler.
      const itemField =
        l.type === "INGREDIENT" ? { ingredient: l.refId }
        : l.type === "PRODUCT" ? { product: l.refId }
        : { supply_item: l.refId };
      return {
        ...itemField,
        quantity_received: quantityReceived,
        unit_price_minor: unitPriceMinor,
        ...(l.poLineId ? { po_line: l.poLineId } : {}),
      };
    });
    try {
      await create.mutateAsync({
        supplier: supplierId,
        location: locationId,
        delivery_note_number: deliveryNote,
        ...(prefill?.purchaseOrderId ? { purchase_order: prefill.purchaseOrderId } : {}),
        lines: payloadLines,
      });
      if (prefill?.purchaseOrderId) {
        qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      }
      qc.invalidateQueries({ queryKey: ["goods-receipts"] });
      notifications.show({
        message: "Mal kabul oluşturuldu (taslak) — onaylayınca stok/maliyet işlenir",
        color: "green",
      });
      resetForm();
      onClose();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Mal kabul oluşturulamadı",
        color: "red",
      });
    }
  }

  // Toplam tutar (vergisiz) — alış birimi cinsinden, kuruş kesin. Yalnız
  // işaretli satırlar sayılır (kaydedilecek olanlar); işaretsiz satır toplama
  // katkıda bulunmaz.
  const totalMinor = lines.reduce(
    (sum, l) => sum + (l.selected
      ? Math.round(Number(l.quantity) * Number(l.purchasePriceMinor))
      : 0), 0,
  );

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={isPrefilled ? "PO'dan Mal Girişi (İrsaliye)" : "Yeni Mal Kabul (İrsaliye)"}
        size="xl"
      >
        <Stack gap="md">
          <Group grow align="flex-end">
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Tedarikçi <Text span c="red">*</Text>
              </Text>
              {/* PO'dan açıldığında tedarikçi kilitli (PO'nun tedarikçisi). */}
              <Button
                variant="default"
                justify="space-between"
                rightSection={isPrefilled ? null : <IconSearch size={16} />}
                onClick={() => !isPrefilled && setSupplierPickerOpen(true)}
                disabled={isPrefilled}
                styles={{ label: { overflow: "hidden", textOverflow: "ellipsis" } }}
              >
                {supplierId ? supplierName : "Tedarikçi seç"}
              </Button>
            </Stack>
            <Select
              label="Depo / Lokasyon"
              placeholder="Depo seç"
              required
              data={locations.map((l) => ({ value: l.id, label: l.name }))}
              value={locationId}
              onChange={setLocationId}
              searchable
            />
          </Group>
          <TextInput
            label="İrsaliye No"
            placeholder="Tedarikçinin irsaliye numarası (opsiyonel)"
            value={deliveryNote}
            onChange={(e) => setDeliveryNote(e.currentTarget.value)}
          />

          {/* Satır ekleme — tip seç, picker aç. PO'dan açıldıysa satırlar
              kalanlarla zaten dolu; serbest ekleme gizlenir. */}
          {!isPrefilled && (
            <Group align="flex-end" gap="sm">
              <SegmentedControl
                value={newType}
                onChange={(v) => setNewType(v as LineType)}
                data={[
                  { label: "Hammadde", value: "INGREDIENT" },
                  { label: "Hazır Ürün", value: "PRODUCT" },
                  { label: "Mutfak-dışı", value: "SUPPLY_ITEM" },
                ]}
              />
              <Button
                variant="light"
                leftSection={<IconSearch size={16} />}
                onClick={() =>
                  newType === "INGREDIENT" ? setIngPickerOpen(true)
                  : newType === "PRODUCT" ? setProdPickerOpen(true)
                  : setSupplyPickerOpen(true)
                }
              >
                {newType === "INGREDIENT" ? "Hammadde Ekle"
                  : newType === "PRODUCT" ? "Ürün Ekle"
                  : "Mutfak-dışı Ekle"}
              </Button>
            </Group>
          )}
          {isPrefilled && (
            <Text size="xs" c="dimmed">
              Satırlar PO'nun kalan miktarlarıyla dolduruldu. Bu partide gelmeyen
              kalemin işaretini kaldırın (ya da miktarını 0 yapın) — parçalı
              teslimat; fiyat farklı çıktıysa birim fiyatı güncelleyin.
            </Text>
          )}

          {/* Satırlar */}
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={36}>
                  {/* Tümünü seç / bırak */}
                  <Checkbox
                    aria-label="Tümünü seç"
                    checked={lines.length > 0 && lines.every((l) => l.selected)}
                    indeterminate={lines.some((l) => l.selected) && !lines.every((l) => l.selected)}
                    onChange={(e) => {
                      const v = e.currentTarget.checked;
                      setLines((prev) => prev.map((l) => ({ ...l, selected: v })));
                    }}
                  />
                </Table.Th>
                <Table.Th>Kalem</Table.Th>
                <Table.Th>Tip</Table.Th>
                <Table.Th w={110}>Miktar</Table.Th>
                <Table.Th w={90}>Alış Birimi</Table.Th>
                <Table.Th w={150}>Birim Fiyat</Table.Th>
                <Table.Th ta="right">Satır Tutarı</Table.Th>
                <Table.Th w={44}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((l) => (
                <Table.Tr key={l.key} opacity={l.selected ? 1 : 0.45}>
                  <Table.Td>
                    {/* Bu satır bu sevkiyata dahil mi? İşaretsiz → kaydedilmez. */}
                    <Checkbox
                      aria-label={`${l.refName} dahil`}
                      checked={l.selected}
                      onChange={(e) => updateLine(l.key, { selected: e.currentTarget.checked })}
                    />
                  </Table.Td>
                  <Table.Td>{l.refName}</Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={
                        l.type === "PRODUCT" ? "violet"
                        : l.type === "SUPPLY_ITEM" ? "teal"
                        : "blue"
                      }
                    >
                      {l.type === "PRODUCT" ? "Ürün"
                        : l.type === "SUPPLY_ITEM" ? "Mutfak-dışı"
                        : "Hammadde"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {/* Miktar — alış birimi cinsinden (örn. kg sayısı). Birim
                        ayrı "Alış Birimi" kolonunda gösterildiği için burada
                        suffix YOK (yoksa "10 kg" + kolon "kg" → çift birim). */}
                    <NumberInput
                      value={l.quantity}
                      onChange={(v) => updateLine(l.key, { quantity: v })}
                      min={0}
                      step={0.1}
                      decimalScale={3}
                      size="xs"
                      {...(l.maxRemaining != null ? { max: l.maxRemaining } : {})}
                      {...(l.maxRemaining != null && Number(l.quantity) > l.maxRemaining + 1e-9
                        ? { error: "kalan aşıldı" }
                        : {})}
                    />
                    {l.maxRemaining != null && (
                      <Text size="10px" c="dimmed" mt={2}>
                        kalan: {l.maxRemaining} {l.stockUnit}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {/* Alış birimi seçimi. Tek seçenek varsa (kg/lt/adet stok
                        birimi) düz etiket gösterilir — dropdown gereksiz. */}
                    {purchaseUnitsFor(l.stockUnit).length > 1 ? (
                      <Select
                        value={l.purchaseUnit}
                        onChange={(v) => v && updateLine(l.key, { purchaseUnit: v })}
                        data={purchaseUnitsFor(l.stockUnit).map((o) => ({
                          value: o.value, label: o.label,
                        }))}
                        size="xs"
                        allowDeselect={false}
                      />
                    ) : (
                      <Text size="xs" c="dimmed">{l.purchaseUnit}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {/* Fiyat ALIŞ birimi başına lira girilir (örn. ₺/kg),
                        kuruşa çevrilir. Stok birimi farklıysa altta gram-başı
                        maliyet ipucu gösterilir. */}
                    <NumberInput
                      value={Number(l.purchasePriceMinor) / 100}
                      onChange={(v) =>
                        updateLine(l.key, { purchasePriceMinor: Math.round(Number(v) * 100) })
                      }
                      min={0}
                      decimalScale={2}
                      fixedDecimalScale
                      suffix={` ₺/${l.purchaseUnit}`}
                      size="xs"
                    />
                    {l.purchaseUnit !== l.stockUnit && Number(l.purchasePriceMinor) > 0 && (
                      <Text size="10px" c="dimmed" mt={2}>
                        ≈ {(
                          Number(l.purchasePriceMinor) /
                          100 /
                          factorFor(l.stockUnit, l.purchaseUnit)
                        ).toLocaleString("tr-TR", { maximumFractionDigits: 4 })} ₺/{l.stockUnit}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right" fw={600}>
                    <MoneyDisplay minor={Math.round(Number(l.quantity) * Number(l.purchasePriceMinor))} />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => removeLine(l.key)}
                      aria-label="Satırı sil"
                    >
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
              {lines.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8} ta="center" c="dimmed">
                    Henüz satır eklenmedi — yukarıdan hammadde veya ürün ekleyin
                  </Table.Td>
                </Table.Tr>
              )}
              {lines.length > 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6} ta="right" fw={700}>Toplam</Table.Td>
                  <Table.Td ta="right" fw={800} c="teal">
                    <MoneyDisplay minor={totalMinor} />
                  </Table.Td>
                  <Table.Td />
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>

          <Text size="xs" c="dimmed">
            Fiyatı faturadaki alış birimiyle girin (örn. 1 kg = 123 ₺). Stok
            ve reçete daha küçük birimde tutuluyorsa (gr/ml) sistem otomatik
            çevirir — 1 kg girişi stoğa 1000 gr olarak işlenir. Kayıt taslak
            (DRAFT) oluşur; "Onayla" dediğinizde stok artar ve maliyetler
            güncellenir.
          </Text>

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>İptal</Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleSubmit}
              loading={create.isPending}
            >
              Mal Kabul Oluştur
            </Button>
          </Group>
        </Stack>
      </Modal>

      <SupplierPickerModal
        opened={supplierPickerOpen}
        onClose={() => setSupplierPickerOpen(false)}
        onSelect={(s) => { setSupplierId(s.id); setSupplierName(s.name); }}
      />
      <IngredientPickerModal
        opened={ingPickerOpen}
        onClose={() => setIngPickerOpen(false)}
        onSelect={(ing) => addLine("INGREDIENT", ing.id, ing.name, ing.unit)}
        excludeIds={lines.filter((l) => l.type === "INGREDIENT").map((l) => l.refId)}
      />
      <ProductPickerModal
        opened={prodPickerOpen}
        onClose={() => setProdPickerOpen(false)}
        onSelect={(p) => addLine("PRODUCT", p.id, p.name, "adet")}
        excludeIds={lines.filter((l) => l.type === "PRODUCT").map((l) => l.refId)}
      />
      <SupplyItemPickerModal
        opened={supplyPickerOpen}
        onClose={() => setSupplyPickerOpen(false)}
        onSelect={(si) => addLine("SUPPLY_ITEM", si.id, si.name, si.unit)}
        excludeIds={lines.filter((l) => l.type === "SUPPLY_ITEM").map((l) => l.refId)}
      />
    </>
  );
}
